// Lets the browser/system Back button close sheets, dialogs and menus first,
// the way Android back pops a Flutter modal route. Each open overlay pushes a
// same-URL history entry tagged with its depth; popping that entry closes the
// overlay. Closing an overlay in-app removes its entry again (history.back()).
//
// history.back() is asynchronous, so every mutation is serialised: while one of
// our traversals is in flight, later pushes and releases wait for its popstate.
// Otherwise a pushState issued in the same tick (close a sheet, open a dialog)
// would be popped by the pending back() and the new overlay closed at once.
//
// A release decides whether to go back in a microtask: a navigation made
// synchronously alongside the close (a menu releases its entry, then runs the
// row's action, which pushes a page) lands first, the entry is then no longer
// on top, and Back skips it later instead of the pending back() undoing that
// navigation. Still before any popstate, so a close followed by a history
// step back (nav.back()) keeps going back twice, as before.

const STATE_KEY = '__plumeOverlay';
/** Fallback for environments where popstate never arrives. */
const POP_FALLBACK_MS = 400;

interface Entry {
  depth: number;
  onPop: () => void;
}

const stack: Entry[] = [];
const queue: Array<() => void> = [];
const idleWaiters: Array<() => void> = [];
/** Non-null while one of our history.back() / go() calls is in flight. */
let traversalTimer: ReturnType<typeof setTimeout> | null = null;
/** True while a release waits for the current task's synchronous code before deciding whether to go back. */
let releasePending = false;
let listening = false;

function busy(): boolean {
  return traversalTimer !== null || releasePending;
}

function stateDepth(state: unknown): number {
  if (state && typeof state === 'object' && STATE_KEY in state) {
    const depth = (state as Record<string, unknown>)[STATE_KEY];
    return typeof depth === 'number' ? depth : 0;
  }
  return 0;
}

function currentDepth(): number {
  return stateDepth(typeof history !== 'undefined' ? history.state : null);
}

function drain(): void {
  while (!busy() && queue.length > 0) queue.shift()?.();
  if (!busy()) idleWaiters.splice(0).forEach((resolve) => resolve());
}

/** Runs `op` now, or once the in-flight traversal has landed. */
function schedule(op: () => void): void {
  if (busy()) queue.push(op);
  else op();
}

function traverse(delta: number, onFallback?: () => void): void {
  traversalTimer = setTimeout(() => {
    traversalTimer = null;
    onFallback?.();
    drain();
  }, POP_FALLBACK_MS);
  history.go(delta);
}

function traverseBack(): void {
  traverse(-1);
}

function onPopState(): void {
  if (traversalTimer !== null) {
    clearTimeout(traversalTimer);
    traversalTimer = null;
  }
  const depth = currentDepth();
  for (let i = stack.length - 1; i >= 0; i--) {
    const entry = stack[i];
    if (entry && entry.depth > depth) {
      stack.splice(i, 1);
      entry.onPop();
    }
  }
  // Landed on the entry of an overlay that was closed while another page was on
  // top (or that a reload left behind): skip past it so Back does not appear to
  // do nothing.
  if (depth > 0 && !stack.some((entry) => entry.depth === depth)) {
    traverseBack();
    return;
  }
  drain();
}

function ensureListener(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('popstate', onPopState);
}

/**
 * Resolves once no overlay history traversal is pending. Await it before a
 * navigation that follows an in-app close (dialog promises already do), or
 * the pending history.back() would undo that navigation.
 */
export function whenOverlayHistoryIdle(): Promise<void> {
  if (!busy() && queue.length === 0) return Promise.resolve();
  return new Promise((resolve) => idleWaiters.push(resolve));
}

/**
 * A reload (or the browser discarding and restoring the tab) while an overlay
 * was open lands on that overlay's history entry with nothing open. Steps back
 * to the entry underneath, so the first Back press is not a dead one; where
 * that is impossible the overlay tag is dropped instead. Runs once when this
 * module loads, before the navigator mounts; also starts the popstate listener
 * so entries left behind under a page are skipped after a reload.
 */
export function recoverOverlayHistory(): void {
  if (typeof window === 'undefined' || typeof history === 'undefined') return;
  ensureListener();
  const depth = currentDepth();
  if (depth <= 0 || stack.length > 0 || busy()) return;
  traverse(-depth, () => {
    // The browser could not go back (e.g. a restored tab without the entries
    // underneath): keep this entry but stop treating it as an overlay's.
    const state = history.state as Record<string, unknown> | null;
    if (stateDepth(state) > 0 && state) {
      const { [STATE_KEY]: _dropped, ...rest } = state;
      history.replaceState(rest, '');
    }
  });
}

export interface OverlayHistoryHandle {
  /** Removes the overlay's history entry (if it is current). Resolves when history is settled. */
  release(): Promise<void>;
}

/** Registers an open overlay. `onPop` runs when Back pops its entry. */
export function pushOverlayEntry(onPop: () => void): OverlayHistoryHandle {
  if (typeof window === 'undefined') return { release: () => Promise.resolve() };
  ensureListener();
  const entry: Entry = { depth: 0, onPop };
  let phase: 'queued' | 'active' | 'released' = 'queued';
  schedule(() => {
    if (phase !== 'queued') return;
    phase = 'active';
    entry.depth = currentDepth() + 1;
    const base = history.state && typeof history.state === 'object' ? history.state : {};
    history.pushState({ ...base, [STATE_KEY]: entry.depth }, '');
    stack.push(entry);
  });
  let released: Promise<void> | null = null;
  return {
    release() {
      if (released) return released;
      const index = stack.indexOf(entry);
      phase = 'released';
      // Not in the stack: never pushed, or already popped by Back.
      if (index >= 0) {
        stack.splice(index, 1);
        schedule(() => {
          releasePending = true;
          queueMicrotask(() => {
            releasePending = false;
            // Not on top (a page or overlay was pushed above it, or Back already
            // left it): Back skips it later.
            if (currentDepth() === entry.depth) traverseBack();
            else drain();
          });
        });
      }
      released = whenOverlayHistoryIdle();
      return released;
    },
  };
}

const hot = import.meta.hot;
// Only on the real page load: a hot update re-evaluates this module while an
// overlay may legitimately be open.
if (hot?.data?.overlayHistoryLoaded) ensureListener();
else recoverOverlayHistory();
if (hot) {
  if (hot.data) hot.data.overlayHistoryLoaded = true;
  // The fresh copy registers its own listener; the old one would treat every
  // overlay entry as one left behind.
  hot.dispose(() => {
    if (typeof window !== 'undefined') window.removeEventListener('popstate', onPopState);
  });
}
